import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  getAllSessions,
  getAllRecommendations,
  exportToCSV,
  clearAllAnalytics,
  type AnalyticsSession,
  type RecommendationRecord,
} from "@/lib/analyticsDB";
import { seedAnalyticsData } from "./lib/seedData";
import StatsCards from "./components/StatsCards";
import FunnelChart from "./components/FunnelChart";
import ProductPopularity from "./components/ProductPopularity";
import ChallengeDistribution from "./components/ChallengeDistribution";
import UserSegments from "./components/UserSegments";
import SessionsTable from "./components/SessionsTable";
import QuestionBranching from "./components/QuestionBranching";
import AdminGate from "@/components/feature/AdminGate";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function AdvisorAnalyticsPage() {
  return (
    <AdminGate pageName="Product Advisor Analytics">
      <AnalyticsContent />
    </AdminGate>
  );
}

function AnalyticsContent() {
  const [sessions, setSessions] = useState<AnalyticsSession[]>([]);
  const [recs, setRecs] = useState<RecommendationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeded, setSeeded] = useState(false);
  const [showConfirmClear, setShowConfirmClear] = useState(false);

  usePageMeta(
    "Product Advisor Analytics | StackMap Systems",
    "Internal analytics dashboard for the StackMap Product Advisor. Review session data, recommendations, and user insights."
  );

  const loadData = useCallback(async () => {
    const [s, r] = await Promise.all([getAllSessions(), getAllRecommendations()]);
    setSessions(s);
    setRecs(r);
    setLoading(false);
  }, []);

  useEffect(() => {
    seedAnalyticsData().then((wasSeeded) => {
      setSeeded(wasSeeded);
      loadData();
    });
  }, [loadData]);

  const handleExport = () => {
    const csv = exportToCSV(sessions);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stackmap-advisor-sessions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClear = async () => {
    await clearAllAnalytics();
    setShowConfirmClear(false);
    loadData();
  };

  return (
    <div className="min-h-screen bg-graphite-900 text-silver-200">
      {/* Header */}
      <header className="border-b border-graphite-700/50 bg-graphite-900/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="section-padding max-w-7xl mx-auto py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-graphite-800/60 border border-graphite-700/50 text-silver-400 hover:text-white transition-colors"
            >
              <i className="ri-arrow-left-line" />
            </Link>
            <div>
              <h1 className="text-lg font-bold text-white">Product Advisor Analytics</h1>
              <p className="text-xs text-silver-400">Understand how visitors interact with the StackMap Product Advisor</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="btn-outline text-xs px-3 py-2"
              disabled={sessions.length === 0}
            >
              <i className="ri-download-line" />
              Export CSV
            </button>
            <button
              onClick={() => setShowConfirmClear(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-medium rounded-lg hover:bg-red-500/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line" />
              Clear Data
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="section-padding max-w-7xl mx-auto py-6 md:py-8 space-y-6">
        {seeded && (
          <div className="px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs text-blue-400">
            <i className="ri-information-line mr-1" />
            Demo data has been seeded so you can explore the analytics. Use the Product Advisor on any page to generate real data.
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-emerald-accent text-2xl" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="glass-card p-8 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-graphite-700/50 mx-auto mb-4">
              <i className="ri-bar-chart-box-line text-2xl text-silver-400" />
            </div>
            <h3 className="text-base font-bold text-white mb-1">No Data Yet</h3>
            <p className="text-sm text-silver-400 max-w-md mx-auto mb-5">
              Once visitors start using the Product Advisor, you'll see real-time analytics here. Try opening the advisor yourself first.
            </p>
            <button
              onClick={async () => {
                const wasSeeded = await seedAnalyticsData();
                if (wasSeeded) {
                  setSeeded(true);
                  loadData();
                }
              }}
              className="btn-primary"
            >
              <i className="ri-database-2-line" />
              Load Demo Data
            </button>
          </div>
        ) : (
          <>
            <StatsCards sessions={sessions} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <FunnelChart sessions={sessions} />
              <ChallengeDistribution sessions={sessions} />
            </div>

            <QuestionBranching sessions={sessions} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ProductPopularity recs={recs} />
              <UserSegments sessions={sessions} />
            </div>

            {/* Top Problems + Demand Trends mini insight */}
            <div className="glass-card p-4 md:p-5">
              <h3 className="text-base font-bold text-white mb-1">Product Demand Trends</h3>
              <p className="text-xs text-silver-400 mb-4">Demand shifts based on user challenges and industry</p>
              <DemandTrends sessions={sessions} />
            </div>

            <SessionsTable sessions={sessions} />
          </>
        )}
      </main>

      {/* Confirm Clear Modal */}
      {showConfirmClear && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="glass-card p-5 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-500/10 border border-red-500/20">
                <i className="ri-alert-line text-red-400" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-white">Clear all analytics?</h4>
                <p className="text-xs text-silver-400">This permanently deletes all session data.</p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setShowConfirmClear(false)}
                className="btn-outline flex-1 justify-center text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleClear}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-red-500 text-white text-xs font-semibold rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap flex-1"
              >
                <i className="ri-delete-bin-line" />
                Clear Data
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DemandTrends({ sessions }: { sessions: AnalyticsSession[] }) {
  // Cross-tab: challenges vs recommended products
  const challengeProductMap = new Map<string, Map<string, number>>();

  for (const s of sessions) {
    const challenges = s.answers?.challenges;
    const products = s.productsRecommended || [];
    let challengeList: string[] = [];
    if (Array.isArray(challenges)) {
      challengeList = challenges.filter((c) => typeof c === "string") as string[];
    } else if (typeof challenges === "string") {
      challengeList = [challenges];
    }

    for (const c of challengeList) {
      if (!challengeProductMap.has(c)) {
        challengeProductMap.set(c, new Map<string, number>());
      }
      const pm = challengeProductMap.get(c)!;
      for (const p of products) {
        pm.set(p, (pm.get(p) || 0) + 1);
      }
    }
  }

  const CHALLENGE_NAMES: Record<string, string> = {
    staff: "Staff management",
    operations: "Operations tracking",
    reporting: "Reporting & visibility",
    billing: "Billing & revenue",
    performance: "Performance management",
  };

  const topChallenges = Array.from(challengeProductMap.entries())
    .map(([challenge, pm]) => {
      const total = Array.from(pm.values()).reduce((a, b) => a + b, 0);
      const topProduct = Array.from(pm.entries()).sort((a, b) => b[1] - a[1])[0];
      return {
        challenge: CHALLENGE_NAMES[challenge] || challenge,
        total,
        topProduct: topProduct?.[0] || "N/A",
        topCount: topProduct?.[1] || 0,
      };
    })
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);

  if (topChallenges.length === 0) {
    return <p className="text-sm text-silver-400 text-center py-6">Not enough data to show demand trends.</p>;
  }

  const COLORS = ["#10b981", "#059669", "#34d399", "#14b8a6", "#0ea5e9"];

  return (
    <div className="space-y-3">
      {topChallenges.map((row, i) => (
        <div key={row.challenge} className="flex items-center gap-3">
          <span className="text-xs text-silver-300 w-32 flex-shrink-0">{row.challenge}</span>
          <div className="flex-1 h-2 bg-graphite-700/60 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${Math.min(100, row.total * 8)}%`, backgroundColor: COLORS[i % COLORS.length] }}
            />
          </div>
          <span className="text-[11px] text-silver-400">Top pick:</span>
          <span className="text-xs font-medium text-white whitespace-nowrap">{row.topProduct.replace("StackMap ", "")}</span>
          <span className="text-xs text-silver-500 w-6 text-right">{row.total}</span>
        </div>
      ))}
    </div>
  );
}